import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Brain, Target, Heart, Users, CheckCircle, Sparkles, 
  ArrowLeft, Shield, TrendingUp, MessageCircle, Star
} from "lucide-react";
import { Carousel } from "../../components/ui/carousel";

const HowItWorks: React.FC = () => {
  const [dimensionIndex, setDimensionIndex] = useState(0);
  const [domainIndex, setDomainIndex] = useState(0);
  const [lessonIndex, setLessonIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset domain and lesson indices when dimension changes
  useEffect(() => {
    setDomainIndex(0);
    setLessonIndex(0);
  }, [dimensionIndex]);

  // Reset lesson index when domain changes
  useEffect(() => {
    setLessonIndex(0);
  }, [domainIndex]);

  // Define the curriculum structure with all dimensions, domains, and lessons
  const curriculumStructure = [
    {
      name: "Individual & Psychological",
      color: "from-blue-500 to-cyan-600",
      lineColor: "#3b82f6",
      domains: [
        {
          name: "Self-Awareness",
          lessons: ["Understanding Your Triggers", "Recognizing Emotion Patterns", "Your Values Inventory", "Strength Spotting", "Journaling for Insight"],
        },
        {
          name: "Emotional Fitness",
          lessons: ["Riding the Emotion Wave", "The Calm Down Toolkit", "Processing Difficult Feelings", "Building Emotional Stamina", "Reframing Negative Thoughts"],
        },
        {
          name: "Decision Making",
          lessons: ["The 10-10-10 Rule", "Pros & Cons Done Right", "Trusting Your Gut", "When to Seek Advice", "Learning From Past Choices"],
        },
        {
          name: "Growth Mindset",
          lessons: ["The Power of Yet", "Embracing Mistakes", "Effort vs. Talent", "Challenge as Opportunity", "Building Mental Toughness"],
        },
        {
          name: "Purpose & Meaning",
          lessons: ["Finding Your Why", "Values-Based Living", "Creating a Personal Mission", "What Drives You", "Living With Intent"],
        },
      ],
    },
    {
      name: "Social & Environmental",
      color: "from-pink-500 to-purple-600",
      lineColor: "#a855f7",
      domains: [
        {
          name: "Communication Boundaries",
          lessons: ["Saying No Without Guilt", "Setting Limits Clearly", "When to Walk Away", "Protecting Your Energy", "Expressing Needs"],
        },
        {
          name: "Social Connections",
          lessons: ["Making Genuine Friends", "Being a Good Listener", "Deepening Relationships", "Finding Your People", "Showing Up for Others"],
        },
        {
          name: "Media Literacy",
          lessons: ["Spotting Clickbait", "Fact-Checking Basics", "Identifying Bias", "Reliable vs. Unreliable Sources", "Understanding Algorithms"],
        },
        {
          name: "Digital Safety",
          lessons: ["Privacy Settings That Matter", "Recognizing Online Scams", "Protecting Your Data", "Safe Social Media Use", "Cyberbullying Response"],
        },
        {
          name: "Providing Feedback",
          lessons: ["The Sandwich Method", "Constructive vs. Critical", "Timing Your Feedback", "Being Specific and Kind", "Following Up"],
        },
      ],
    },
    {
      name: "Behavioral & Physical",
      color: "from-green-500 to-emerald-600",
      lineColor: "#10b981",
      domains: [
        {
          name: "Habits",
          lessons: ["The 2-Minute Rule", "Habit Stacking", "Breaking Bad Habits", "Designing Your Environment", "Tracking Progress"],
        },
        {
          name: "Routines & Productivity",
          lessons: ["Morning Routine Power", "Time Blocking Basics", "Managing Distractions", "Energy Management", "Weekly Planning"],
        },
        {
          name: "Planning & Execution",
          lessons: ["Setting SMART Goals", "Breaking Down Big Projects", "Staying on Track", "Accountability Systems", "Celebrating Milestones"],
        },
        {
          name: "Self-Care & Physical Health",
          lessons: ["Sleep Hygiene 101", "Movement That Works", "Eating for Energy", "Stress Relief Practices", "Screen Time Balance"],
        },
        {
          name: "Financial Responsibility",
          lessons: ["Budgeting Basics", "Needs vs. Wants", "Saving Strategies", "Understanding Credit", "Smart Spending Habits"],
        },
      ],
    },
  ];

  const currentDimension = curriculumStructure[dimensionIndex];
  const currentDomain = currentDimension.domains[domainIndex];
  const currentLesson = currentDomain.lessons[lessonIndex];

  const dimensionGroups = [
    {
      dimension: "Individual & Psychological",
      subtitle: "Inner skills like self-awareness, emotional regulation, and thinking patterns",
      domains: [
        { icon: Brain, title: "Self-Awareness", desc: "Understand your thoughts, emotions, and patterns", color: "from-blue-600 to-cyan-600" },
        { icon: Star, title: "Self-Worth", desc: "Build unconditional confidence in your value", color: "from-blue-600 to-cyan-600" },
        { icon: Heart, title: "Emotional Fitness", desc: "Strengthen coping skills and emotional resilience", color: "from-blue-600 to-cyan-600" },
        { icon: Heart, title: "Healthy Coping Behaviors", desc: "Manage stress and difficult emotions effectively", color: "from-blue-600 to-cyan-600" },
        { icon: Brain, title: "Critical Thinking & Reasoning", desc: "Analyze information and make sound judgments", color: "from-blue-600 to-cyan-600" },
        { icon: Target, title: "Decision Making", desc: "Make clear, confident choices under pressure", color: "from-blue-600 to-cyan-600" },
        { icon: Sparkles, title: "Optimism & Growth Mindset", desc: "View challenges as opportunities to grow", color: "from-blue-600 to-cyan-600" },
        { icon: Star, title: "Expectation Management", desc: "Set realistic expectations and reduce disappointment", color: "from-blue-600 to-cyan-600" },
        { icon: Star, title: "Purpose & Meaning", desc: "Discover your values and what drives you", color: "from-blue-600 to-cyan-600" },
      ]
    },
    {
      dimension: "Social & Environmental",
      subtitle: "Relationships, communication, community, and the information environment",
      domains: [
        { icon: MessageCircle, title: "Communication Boundaries", desc: "Express needs and maintain healthy limits", color: "from-pink-600 to-purple-600" },
        { icon: Users, title: "Social Connections & Belonging", desc: "Build meaningful relationships and community", color: "from-pink-600 to-purple-600" },
        { icon: MessageCircle, title: "Providing Feedback", desc: "Give constructive criticism with care", color: "from-pink-600 to-purple-600" },
        { icon: MessageCircle, title: "Managing Criticism & Feedback", desc: "Receive and learn from others' input", color: "from-pink-600 to-purple-600" },
        { icon: Brain, title: "Media Literacy", desc: "Detect misinformation and unreliable sources", color: "from-pink-600 to-purple-600" },
        { icon: Shield, title: "Online & Digital Safety", desc: "Protect yourself in digital spaces", color: "from-pink-600 to-purple-600" },
        { icon: Users, title: "Reliability & Teamwork", desc: "Be dependable and collaborate effectively", color: "from-pink-600 to-purple-600" },
        { icon: Shield, title: "Safety & Help-Seeking", desc: "Know when and how to ask for support", color: "from-pink-600 to-purple-600" },
      ]
    },
    {
      dimension: "Behavioral & Physical",
      subtitle: "Habits, routines, self-care, planning, and practical life skills",
      domains: [
        { icon: Target, title: "Habits", desc: "Build sustainable routines that stick", color: "from-green-600 to-emerald-600" },
        { icon: Star, title: "Routines & Productivity", desc: "Structure your day for maximum effectiveness", color: "from-green-600 to-emerald-600" },
        { icon: Star, title: "Planning & Execution", desc: "Set goals and follow through consistently", color: "from-green-600 to-emerald-600" },
        { icon: Sparkles, title: "Flexibility & Adaptability", desc: "Adjust when plans change unexpectedly", color: "from-green-600 to-emerald-600" },
        { icon: Heart, title: "Self-Care & Physical Health", desc: "Nurture your body and overall wellness", color: "from-green-600 to-emerald-600" },
        { icon: TrendingUp, title: "Financial Responsibility", desc: "Manage money and build financial security", color: "from-green-600 to-emerald-600" },
        { icon: Shield, title: "Personal Responsibility", desc: "Take ownership of your choices and actions", color: "from-green-600 to-emerald-600" },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Back Button */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-4 transition">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-2">How ZillHub Works</h1>
          <p className="text-xl text-indigo-100">Understanding our curriculum structure and approach</p>
        </div>
      </div>

      {/* Curriculum Structure */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full border border-blue-200 mb-4">
              <Target className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-900">Curriculum Structure</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6">
              Organized by
              <span className="block text-transparent bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text">
                Real-Life Experience
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our curriculum is organized first by Dimensions, then Domains, then Lessons. This hierarchy reflects how people experience life.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 border-2 border-blue-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  1
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Dimensions (3 total)</h3>
                  <p className="text-slate-700 text-lg leading-relaxed">
                    The broadest categories: <span className="font-semibold">Individual & Psychological</span>, <span className="font-semibold">Social & Environmental</span>, and <span className="font-semibold">Behavioral & Physical</span>. These represent major areas of life.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border-2 border-purple-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  2
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Domains (24 total)</h3>
                  <p className="text-slate-700 text-lg leading-relaxed">
                    Specific life skill areas within each dimension, like <span className="font-semibold">Communication Boundaries</span>, <span className="font-semibold">Emotional Fitness</span>, or <span className="font-semibold">Decision Making</span>. These are the core skills you practice.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-8 border-2 border-pink-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  3
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Lessons (800+ total)</h3>
                  <p className="text-slate-700 text-lg leading-relaxed">
                    Short, interactive lessons that build skills progressively from Intro to Advanced levels. Each lesson focuses on one practical skill you can use right away.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It All Fits Together - Interactive Nested Carousels */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl p-8 md:p-12 border-2 border-purple-200 max-w-6xl mx-auto">
            <h3 className="text-2xl font-bold text-slate-900 mb-2 text-center">How It All Fits Together</h3>
            <p className="text-center text-slate-600 mb-8 text-sm">Use the arrows to explore different paths through the curriculum</p>
            
            {/* Hierarchical Flow with Nested Carousels */}
            <div className="relative flex flex-col items-center gap-8">
              
              {/* Level 1: Dimensions Carousel */}
              <div className="w-full">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide text-center mb-3">
                  Level 1: Dimensions ({curriculumStructure.length} total)
                </div>
                <Carousel currentIndex={dimensionIndex} onIndexChange={setDimensionIndex}>
                  {curriculumStructure.map((dimension, idx) => (
                    <div key={idx} className="flex justify-center px-4">
                      <div className={`bg-gradient-to-r ${dimension.color} text-white rounded-lg px-8 py-6 w-64 text-center shadow-lg border-2 border-white/30`}>
                        <div className="text-base font-bold mb-1">{dimension.name}</div>
                        <div className="text-xs opacity-90">{dimension.domains.length} domains</div>
                      </div>
                    </div>
                  ))}
                </Carousel>
              </div>

              {/* Connection Lines from Dimension to Domains */}
              <div className="relative h-12 w-full">
                <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                  <line x1="50%" y1="0" x2="50%" y2="100%" stroke={currentDimension.lineColor} strokeWidth="3" />
                </svg>
              </div>

              {/* Level 2: Domains Carousel */}
              <div className="w-full">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide text-center mb-3">
                  Level 2: Domains ({currentDimension.domains.length} in {currentDimension.name})
                </div>
                <Carousel currentIndex={domainIndex} onIndexChange={setDomainIndex}>
                  {currentDimension.domains.map((domain, idx) => (
                    <div key={idx} className="flex justify-center px-4">
                      <div className={`bg-gradient-to-r ${currentDimension.color} text-white rounded-lg px-6 py-4 w-56 text-center shadow-lg border-2 border-white/30`}>
                        <div className="text-sm font-bold mb-1">{domain.name}</div>
                        <div className="text-xs opacity-90">{domain.lessons.length} lessons</div>
                      </div>
                    </div>
                  ))}
                </Carousel>
              </div>

              {/* Connection Lines from Domain to Lessons */}
              <div className="relative h-12 w-full">
                <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                  <line x1="50%" y1="0" x2="50%" y2="100%" stroke={currentDimension.lineColor} strokeWidth="3" />
                </svg>
              </div>

              {/* Level 3: Lessons Carousel */}
              <div className="w-full">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide text-center mb-3">
                  Level 3: Lessons ({currentDomain.lessons.length} in {currentDomain.name})
                </div>
                <Carousel currentIndex={lessonIndex} onIndexChange={setLessonIndex}>
                  {currentDomain.lessons.map((lesson, idx) => (
                    <div key={idx} className="flex justify-center px-4">
                      <div className={`bg-gradient-to-r ${currentDimension.color} text-white rounded-lg px-5 py-3 w-48 text-center shadow-lg border-2 border-white/30`}>
                        <div className="text-sm font-bold">{lesson}</div>
                      </div>
                    </div>
                  ))}
                </Carousel>
              </div>

              {/* Current Path Display */}
              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-slate-200 max-w-2xl mx-auto mt-4">
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-r ${currentDimension.color}`}>
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">Current Path</h4>
                    <div className="text-sm text-slate-600 space-y-1">
                      <div><span className="font-semibold">Dimension:</span> {currentDimension.name}</div>
                      <div><span className="font-semibold">Domain:</span> {currentDomain.name}</div>
                      <div><span className="font-semibold">Lesson:</span> "{currentLesson}"</div>
                    </div>
                    <p className="text-xs text-slate-600 italic mt-3">
                      Use the arrows at each level to explore different paths through our 800+ lesson curriculum.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Evidence-Based Approaches Section */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full border border-indigo-200 mb-4">
              <Brain className="h-4 w-4 text-indigo-600" />
              <span className="text-sm font-semibold text-indigo-900">Evidence-Based Methods</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6">
              Built on Proven
              <span className="block text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text">
                Therapeutic Approaches
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our lessons integrate research-backed methods from psychology and therapy, adapted for real-world use.
            </p>
          </div>

          <div className="max-w-6xl mx-auto space-y-8">
            {/* First Row: CBT and ACT */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* CBT */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-indigo-300 transition-all hover:-translate-y-1">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 mb-4 shadow-md">
                  <Brain className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">CBT</h3>
                <p className="text-sm text-slate-600 mb-3">Cognitive Behavioral Therapy</p>
                <p className="text-slate-700 leading-relaxed">
                  Challenge unhelpful thought patterns, reframe perspectives, and build practical coping skills through structured exercises.
                </p>
              </div>

              {/* ACT */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-purple-300 transition-all hover:-translate-y-1">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 mb-4 shadow-md">
                  <Heart className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">ACT</h3>
                <p className="text-sm text-slate-600 mb-3">Acceptance &amp; Commitment Therapy</p>
                <p className="text-slate-700 leading-relaxed">
                  Accept difficult emotions, clarify your values, and take committed action toward what matters most to you.
                </p>
              </div>
            </div>

            {/* Second Row: Positive Psychology, Mindfulness, Psychoeducation */}
            <div className="grid md:grid-cols-3 gap-8">
              {/* Positive Psychology */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-amber-300 transition-all hover:-translate-y-1">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 mb-4 shadow-md">
                  <Sparkles className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Positive Psychology</h3>
                <p className="text-sm text-slate-600 mb-3">Strengths-Based Growth</p>
                <p className="text-slate-700 leading-relaxed">
                  Focus on your strengths, build growth mindset, and cultivate well-being through research-backed interventions.
                </p>
              </div>

              {/* Mindfulness */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-teal-300 transition-all hover:-translate-y-1">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 mb-4 shadow-md">
                  <Target className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Mindfulness</h3>
                <p className="text-sm text-slate-600 mb-3">Present-Moment Awareness</p>
                <p className="text-slate-700 leading-relaxed">
                  Develop awareness of thoughts and emotions without judgment, and practice grounding in the present moment.
                </p>
              </div>

              {/* Psychoeducation */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-emerald-300 transition-all hover:-translate-y-1">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 mb-4 shadow-md">
                  <CheckCircle className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Psychoeducation</h3>
                <p className="text-sm text-slate-600 mb-3">Understanding Through Learning</p>
                <p className="text-slate-700 leading-relaxed">
                  Learn how your mind works, understand psychological concepts, and gain insight into your patterns and behaviors.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom note */}
          <div className="mt-12 max-w-3xl mx-auto text-center">
            <p className="text-slate-600 leading-relaxed">
              Each lesson is carefully designed to integrate these approaches based on the skill being taught and your current needs. You get the benefits of therapy-backed methods without the clinical jargon.
            </p>
          </div>
        </div>
      </section>

      {/* 24 Domains Detail */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full border border-blue-200 mb-4">
              <Target className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-900">24 Core Life Skills</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4">
              Show up Strong
              <span className="block text-transparent bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text">
                when it Matters
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Whenever and wherever life tests you.
            </p>
          </div>

          <div className="space-y-16">
            {dimensionGroups.map((group, groupIdx) => (
              <div key={groupIdx}>
                <div className="text-center mb-8">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                    {group.dimension}
                  </h3>
                  <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                    {group.subtitle}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.domains.map((domain, idx) => (
                    <div key={idx} className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-slate-300 shadow-md hover:shadow-xl transition-all hover:-translate-y-2">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r ${domain.color} shadow-md group-hover:scale-110 transition-transform`}>
                          <domain.icon className="h-6 w-6 text-white" />
                        </div>
                        <h4 className="text-lg font-bold text-slate-900">{domain.title}</h4>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">{domain.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is SEL Section */}
      <section className="py-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-80 h-80 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-400 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-blue-200 mb-4 shadow-lg">
              <Heart className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-900">SEL Aligned</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6">
              What is Social & Emotional
              <span className="block text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text">
                Learning (SEL)?
              </span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-xl border border-blue-100">
              <p className="text-xl text-slate-700 leading-relaxed mb-6">
                SEL (Social & Emotional Learning) is a widely recognized framework used in schools and programs worldwide to describe the skills people need to understand emotions, build relationships, and make responsible decisions.
              </p>
              <p className="text-xl text-slate-700 leading-relaxed mb-6">
                Think of SEL as a familiar lens—a way to look at life skills through categories that educators, parents, and researchers already understand and trust.
              </p>
              <p className="text-xl text-slate-700 leading-relaxed">
                <span className="font-bold text-slate-900">In ZillHub:</span> SEL is not the organizing structure of our curriculum. Instead, it's a lesson-level alignment layer. Every lesson is tagged with the SEL competency it aligns to, so you can see how our practical life skills map to this well-known framework.
              </p>
            </div>

            <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl p-8 border border-indigo-200">
              <h3 className="text-xl font-bold text-slate-900 mb-4 text-center">SEL's Five Core Competencies</h3>
              <div className="grid md:grid-cols-5 gap-4 mb-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 mb-2 shadow-lg">
                    <Brain className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-bold text-slate-900 text-sm">Self-Awareness</h4>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 mb-2 shadow-lg">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-bold text-slate-900 text-sm">Self-Management</h4>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 mb-2 shadow-lg">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-bold text-slate-900 text-sm">Social Awareness</h4>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 mb-2 shadow-lg">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-bold text-slate-900 text-sm">Relationship Skills</h4>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 mb-2 shadow-lg">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-bold text-slate-900 text-sm">Responsible Decision-Making</h4>
                </div>
              </div>
              <p className="text-slate-600 text-center italic text-sm">
                These five categories come from CASEL (Collaborative for Academic, Social, and Emotional Learning) and are used in many educational settings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEL in Action - Lesson Examples */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full border border-purple-200 mb-4">
              <Sparkles className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-semibold text-purple-900">SEL in Action</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6">
              How SEL Works
              <span className="block text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text">
                in Our Lessons
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Here are two real lessons from our curriculum, showing how practical life skills connect to SEL competencies.
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-12">
            {/* Example 1 */}
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-8 md:p-10 shadow-xl border-2 border-slate-200">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 rounded-full mb-4">
                    <span className="text-xs font-bold text-blue-900">EXAMPLE LESSON 1</span>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">"Setting a Boundary Without Guilt"</h3>
                  <p className="text-slate-600 mb-4">
                    <span className="font-semibold text-slate-900">Domain:</span> Communication Boundaries
                  </p>
                  <p className="text-slate-600 mb-4">
                    <span className="font-semibold text-slate-900">What you learn:</span> How to say no clearly and kindly without over-explaining or feeling bad about protecting your time and energy.
                  </p>
                  <p className="text-slate-600">
                    <span className="font-semibold text-slate-900">Practical outcome:</span> A simple script for the next time someone asks for something you can't give.
                  </p>
                </div>
                <div className="md:w-80 flex-shrink-0">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200">
                    <div className="text-center mb-4">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 mb-3 shadow-lg">
                        <Heart className="h-7 w-7 text-white" />
                      </div>
                      <h4 className="font-bold text-slate-900 text-lg">SEL Alignment</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                        <p className="text-xs font-semibold text-purple-900 mb-1">Primary Competency</p>
                        <p className="text-sm font-bold text-slate-900">Relationship Skills</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-600 mb-1">Why this maps here</p>
                        <p className="text-xs text-slate-700">Setting boundaries is a core relationship skill—it helps you communicate needs and maintain healthy connections.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Example 2 */}
            <div className="bg-gradient-to-br from-slate-50 to-indigo-50 rounded-3xl p-8 md:p-10 shadow-xl border-2 border-slate-200">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-100 rounded-full mb-4">
                    <span className="text-xs font-bold text-indigo-900">EXAMPLE LESSON 2</span>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">"Recognizing When You're Overwhelmed"</h3>
                  <p className="text-slate-600 mb-4">
                    <span className="font-semibold text-slate-900">Domain:</span> Self-Awareness
                  </p>
                  <p className="text-slate-600 mb-4">
                    <span className="font-semibold text-slate-900">What you learn:</span> How to notice the early signs that stress is building before you reach a breaking point.
                  </p>
                  <p className="text-slate-600">
                    <span className="font-semibold text-slate-900">Practical outcome:</span> A personal checklist of your warning signs and what to do when you spot them.
                  </p>
                </div>
                <div className="md:w-80 flex-shrink-0">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-blue-200">
                    <div className="text-center mb-4">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 mb-3 shadow-lg">
                        <Brain className="h-7 w-7 text-white" />
                      </div>
                      <h4 className="font-bold text-slate-900 text-lg">SEL Alignment</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <p className="text-xs font-semibold text-blue-900 mb-1">Primary Competency</p>
                        <p className="text-sm font-bold text-slate-900">Self-Awareness</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-600 mb-1">Why this maps here</p>
                        <p className="text-xs text-slate-700">Recognizing your internal state is the foundation of self-awareness—knowing what's happening inside you before you react.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Home CTA */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-lg rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
